import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import HybridConditionInput from "@/components/health_conditions/HybridConditionInput";

export function PersonalInfoStep({ formData, updateField, bg }) {
  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Пълно име *" : "Full Name *"}</Label>
        <Input
          placeholder={bg ? "Въведете вашето име" : "Enter your name"}
          value={formData.fullName}
          onChange={(e) => updateField("fullName", e.target.value)}
          className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Възраст *" : "Age *"}</Label>
        <Input
          type="number"
          placeholder={bg ? "Години" : "Years"}
          value={formData.age}
          onChange={(e) => updateField("age", e.target.value)}
          className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Пол *" : "Gender *"}</Label>
        <RadioGroup value={formData.gender} onValueChange={(v) => updateField("gender", v)} className="flex gap-4">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="male" id="male" />
            <Label htmlFor="male" className="cursor-pointer">{bg ? "Мъж" : "Male"}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="female" id="female" />
            <Label htmlFor="female" className="cursor-pointer">{bg ? "Жена" : "Female"}</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

export function BodyMetricsStep({ formData, updateField, bg }) {
  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Ръст (см) *" : "Height (cm) *"}</Label>
        <Input type="number" placeholder={bg ? "напр. 175" : "e.g., 175"}
          value={formData.height} onChange={(e) => updateField("height", e.target.value)}
          className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl" />
      </div>
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Тегло (кг) *" : "Weight (kg) *"}</Label>
        <Input type="number" placeholder={bg ? "напр. 70" : "e.g., 70"}
          value={formData.weight} onChange={(e) => updateField("weight", e.target.value)}
          className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl" />
      </div>
    </div>
  );
}

/**
 * BodyAndMeasurementsStep — Feb 2026 consolidation.
 *
 * Обединена стъпка, която замества старите отделни `BodyMetricsStep`
 * (2 полета) и `MeasurementsStep` (3 полета) в един екран с всичките 5
 * телесни измервания. Използва идентичен API path (progressive save) и
 * идентични field names, за да работи безпроблемно с backend-а.
 *
 * BodyMetricsStep / MeasurementsStep са запазени за обратна съвместимост,
 * ако бъдат ползвани от други flow-ове (тестове, sub-wizards).
 */
export function BodyAndMeasurementsStep({ formData, updateField, bg }) {
  const isMale = formData.gender === "male";
  return (
    <div className="space-y-5 animate-fade-in-up">
      <p className="text-sm text-[#64748B] bg-[#F0F9FF] p-3 rounded-lg">
        {bg
          ? (isMale
              ? "Тези 4 измервания захранват целия ти клиничен профил — DIL бюджета, NIS оценките, BHI и всички здравни индекси. Ще ги виждаш и ще можеш да ги обновяваш във всеки един момент."
              : "Тези 5 измервания захранват целия ти клиничен профил — DIL бюджета, NIS оценките, BHI и всички здравни индекси. Ще ги виждаш и ще можеш да ги обновяваш във всеки един момент.")
          : (isMale
              ? "These 4 measurements power your entire clinical profile — DIL budget, NIS scores, BHI and all clinical indices. You can view and update them at any time."
              : "These 5 measurements power your entire clinical profile — DIL budget, NIS scores, BHI and all clinical indices. You can view and update them at any time.")}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#1A1A2E]">{bg ? "Ръст (см) *" : "Height (cm) *"}</Label>
          <Input type="number" placeholder={bg ? "напр. 175" : "e.g., 175"}
            value={formData.height} onChange={(e) => updateField("height", e.target.value)}
            className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl"
            data-testid="onboarding-height" />
        </div>
        <div className="space-y-2">
          <Label className="text-[#1A1A2E]">{bg ? "Тегло (кг) *" : "Weight (kg) *"}</Label>
          <Input type="number" placeholder={bg ? "напр. 70" : "e.g., 70"}
            value={formData.weight} onChange={(e) => updateField("weight", e.target.value)}
            className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl"
            data-testid="onboarding-weight" />
        </div>
      </div>

      <div className="pt-1">
        <p className="text-xs uppercase tracking-wider font-semibold text-[#64748B] mb-2">
          {bg ? "Обиколки" : "Circumferences"}
        </p>
        <div className={`grid grid-cols-1 gap-4 ${isMale ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
          <div className="space-y-2">
            <Label className="text-[#1A1A2E]">{bg ? "Шия (см) *" : "Neck (cm) *"}</Label>
            <Input type="number" placeholder={bg ? "напр. 38" : "e.g., 38"}
              value={formData.neckCircumference} onChange={(e) => updateField("neckCircumference", e.target.value)}
              className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl"
              data-testid="onboarding-neck" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#1A1A2E]">{bg ? "Талия (см) *" : "Waist (cm) *"}</Label>
            <Input type="number" placeholder={bg ? "напр. 80" : "e.g., 80"}
              value={formData.waistCircumference} onChange={(e) => updateField("waistCircumference", e.target.value)}
              className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl"
              data-testid="onboarding-waist" />
            <p className="text-[10px] text-[#94A3B8]">{bg ? "На нивото на пъпа" : "At navel level"}</p>
          </div>
          {!isMale && (
            <div className="space-y-2">
              <Label className="text-[#1A1A2E]">{bg ? "Ханш (см) *" : "Hip (cm) *"}</Label>
              <Input type="number" placeholder={bg ? "напр. 95" : "e.g., 95"}
                value={formData.hipCircumference} onChange={(e) => updateField("hipCircumference", e.target.value)}
                className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl"
                data-testid="onboarding-hip" />
              <p className="text-[10px] text-[#94A3B8]">{bg ? "В най-широката точка" : "At widest point"}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function MeasurementsStep({ formData, updateField, bg }) {
  return (
    <div className="space-y-6 animate-fade-in-up">
      <p className="text-sm text-[#64748B] bg-[#F0F9FF] p-3 rounded-lg">
        {bg ? "Тези измервания помагат за по-точно изчисляване на телесния ви състав." : "These measurements help us calculate your body composition more accurately."}
      </p>
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Обиколка на шията (см) *" : "Neck Circumference (cm) *"}</Label>
        <Input type="number" placeholder={bg ? "напр. 38" : "e.g., 38"}
          value={formData.neckCircumference} onChange={(e) => updateField("neckCircumference", e.target.value)}
          className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl" />
      </div>
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Обиколка на талията (см) *" : "Waist Circumference (cm) *"}</Label>
        <Input type="number" placeholder={bg ? "напр. 80" : "e.g., 80"}
          value={formData.waistCircumference} onChange={(e) => updateField("waistCircumference", e.target.value)}
          className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl" />
        <p className="text-xs text-[#94A3B8]">{bg ? "Измерете на нивото на пъпа" : "Measure at navel level"}</p>
      </div>
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Обиколка на ханша (см) *" : "Hip Circumference (cm) *"}</Label>
        <Input type="number" placeholder={bg ? "напр. 95" : "e.g., 95"}
          value={formData.hipCircumference} onChange={(e) => updateField("hipCircumference", e.target.value)}
          className="h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-xl" />
        <p className="text-xs text-[#94A3B8]">{bg ? "Измерете в най-широката точка" : "Measure at widest point"}</p>
      </div>
    </div>
  );
}

export function HealthHistoryStep({ formData, updateField, bg }) {
  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Фамилна здравна история" : "Family Health History"}</Label>
        <Textarea
          placeholder={bg ? "Има ли хронични заболявания в семейството? (диабет, сърдечни заболявания и др.)" : "Any chronic conditions in your family? (diabetes, heart disease, etc.)"}
          value={formData.familyHistory} onChange={(e) => updateField("familyHistory", e.target.value)}
          rows={3} className="bg-[#F8FAFC] border-[#E2E8F0] rounded-xl" />
      </div>
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Текущи здравословни проблеми" : "Current Health Conditions"}</Label>
        <HybridConditionInput
          value={formData.currentConditions}
          onChange={(v) => updateField("currentConditions", v)}
          placeholder={bg ? "Имате ли проблеми, с които се справяте в момента? (напр. PCOS, Хашимото, диабет тип 2, фиброаденом ...)" : "Any conditions you're currently managing? (e.g. PCOS, Hashimoto, T2D, fibroadenoma, ...)"}
          rows={3}
          className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm min-h-[80px]"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Медикаменти и добавки" : "Medications & Supplements"}</Label>
        <Textarea
          placeholder={bg ? "Списък на медикаменти или добавки, които приемате" : "List any medications or supplements you take"}
          value={formData.medications} onChange={(e) => updateField("medications", e.target.value)}
          rows={3} className="bg-[#F8FAFC] border-[#E2E8F0] rounded-xl" />
      </div>
    </div>
  );
}

export function LifestyleGoalsStep({ formData, updateField, bg }) {
  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Ниво на активност *" : "Activity Level *"}</Label>
        <RadioGroup value={formData.activityLevel} onValueChange={(v) => updateField("activityLevel", v)} className="space-y-2">
          {[
            { value: "sedentary", label: bg ? "Заседнал" : "Sedentary", desc: bg ? "Малко или никакво упражнение" : "Little or no exercise" },
            { value: "light", label: bg ? "Леко активен" : "Lightly Active", desc: bg ? "Леки упражнения 1-3 дни/седмица" : "Light exercise 1-3 days/week" },
            { value: "moderate", label: bg ? "Умерено активен" : "Moderately Active", desc: bg ? "Умерени упражнения 3-5 дни/седмица" : "Moderate exercise 3-5 days/week" },
            { value: "active", label: bg ? "Много активен" : "Very Active", desc: bg ? "Тежки упражнения 6-7 дни/седмица" : "Hard exercise 6-7 days/week" },
            { value: "very_active", label: bg ? "Извънредно активен" : "Extra Active", desc: bg ? "Много тежки упражнения и физическа работа" : "Very hard exercise & physical job" },
          ].map((item) => (
            <div key={item.value} className="flex items-center space-x-3 p-3 bg-[#F8FAFC] rounded-xl hover:bg-[#F1F5F9] cursor-pointer">
              <RadioGroupItem value={item.value} id={item.value} />
              <div>
                <Label htmlFor={item.value} className="cursor-pointer font-medium">{item.label}</Label>
                <p className="text-xs text-[#94A3B8]">{item.desc}</p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label className="text-[#1A1A2E]">{bg ? "Основна цел *" : "Primary Goal *"}</Label>
        <RadioGroup value={formData.primaryGoal} onValueChange={(v) => updateField("primaryGoal", v)} className="space-y-2">
          {[
            { value: "weight_loss", label: bg ? "Отслабване" : "Weight Loss", desc: bg ? "Загуба на мазнини, запазване на мускулна маса" : "Lose fat while preserving muscle" },
            { value: "muscle_gain", label: bg ? "Мускулна маса" : "Muscle Gain", desc: bg ? "Изграждане на мускулна маса" : "Build muscle mass" },
            { value: "maintenance", label: bg ? "Поддържане на тегло" : "Maintain Weight", desc: bg ? "Запазване на стабилно тегло" : "Keep current weight stable" },
            { value: "general_health", label: bg ? "Общо здраве" : "General Health", desc: bg ? "Подобряване на общото здравословно състояние" : "Improve overall wellness" },
          ].map((item) => (
            <div key={item.value} className="flex items-center space-x-3 p-3 bg-[#F8FAFC] rounded-xl hover:bg-[#F1F5F9] cursor-pointer">
              <RadioGroupItem value={item.value} id={item.value} />
              <div>
                <Label htmlFor={item.value} className="cursor-pointer font-medium">{item.label}</Label>
                <p className="text-xs text-[#94A3B8]">{item.desc}</p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Sleep / Stress / Diet — drive DIL circadian modifier + clinical insights */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
        <div className="space-y-2">
          <Label className="text-[#1A1A2E] text-xs">{bg ? "Сън (часове)" : "Sleep (hours)"}</Label>
          <select
            value={formData.sleepHours}
            onChange={(e) => updateField("sleepHours", e.target.value)}
            className="w-full h-11 px-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-sm"
            data-testid="lifestyle-sleep"
          >
            <option value="<5">{bg ? "<5 ч" : "<5 h"}</option>
            <option value="5-6">{bg ? "5–6 ч" : "5–6 h"}</option>
            <option value="7">{bg ? "7 ч" : "7 h"}</option>
            <option value="8">{bg ? "8 ч" : "8 h"}</option>
            <option value="9+">{bg ? "9+ ч" : "9+ h"}</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label className="text-[#1A1A2E] text-xs">{bg ? "Ниво на стрес" : "Stress level"}</Label>
          <select
            value={formData.stressLevel}
            onChange={(e) => updateField("stressLevel", e.target.value)}
            className="w-full h-11 px-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-sm"
            data-testid="lifestyle-stress"
          >
            <option value="low">{bg ? "Нисък" : "Low"}</option>
            <option value="moderate">{bg ? "Умерен" : "Moderate"}</option>
            <option value="high">{bg ? "Висок" : "High"}</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label className="text-[#1A1A2E] text-xs">{bg ? "Тип хранене" : "Diet type"}</Label>
          <select
            value={formData.dietType}
            onChange={(e) => updateField("dietType", e.target.value)}
            className="w-full h-11 px-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-sm"
            data-testid="lifestyle-diet"
          >
            <option value="mixed">{bg ? "Смесено" : "Mixed"}</option>
            <option value="vegetarian">{bg ? "Вегетарианско" : "Vegetarian"}</option>
            <option value="vegan">{bg ? "Веган" : "Vegan"}</option>
            <option value="keto">{bg ? "Кето" : "Keto"}</option>
            <option value="mediterranean">{bg ? "Средиземноморско" : "Mediterranean"}</option>
          </select>
        </div>
      </div>
    </div>
  );
}
